import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { toast } from '@/components/ui/sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { DraftWorkspace } from '@/components/DraftWorkspace';
import { AppShell } from '@/components/layout/AppShell';
import { useDraft } from '@/hooks/use-draft';
import { usePatchDraft } from '@/hooks/use-patch-draft';
import { usePublishActions } from '@/hooks/use-publish-actions';
import { getToken } from '@/lib/auth';

export const Route = createFileRoute('/drafts/$draftId')({
  beforeLoad: () => {
    if (!getToken()) throw redirect({ to: '/connect' });
  },
  component: DraftPage,
});

function DraftPage() {
  const { draftId } = Route.useParams();
  const draft = useDraft(draftId);
  const patch = usePatchDraft(draftId, draft.data?.website_id);
  const pub = usePublishActions(draftId, draft.data?.website_id);

  if (draft.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <Skeleton className="h-96 w-full max-w-4xl" />
      </div>
    );
  }

  if (draft.isError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-8 text-center">
        <p className="text-destructive">Entwurf nicht gefunden.</p>
        <Link to="/connect" className="text-sm text-muted-foreground hover:underline">
          ← Zur Übersicht
        </Link>
      </div>
    );
  }

  if (!draft.isSuccess) return null;

  return (
    <AppShell websiteId={draft.data.website_id} active="drafts">
      <div className="mx-auto max-w-4xl p-8">
        <DraftWorkspace
          key={draftId}
          draft={draft.data}
          saving={patch.isPending}
          onSave={(p) =>
            patch.mutate(p, {
              onSuccess: () => toast.success('Gespeichert'),
              onError: (e: Error) => toast.error(e.message),
            })
          }
          onPublish={(p) =>
            pub.publish.mutate(p, {
              onSuccess: () => toast.success('Als veröffentlicht markiert'),
              onError: (e: Error) => toast.error(e.message),
            })
          }
          onDiscard={() =>
            pub.discard.mutate(undefined, {
              onSuccess: () => toast.success('Verworfen'),
              onError: (e: Error) => toast.error(e.message),
            })
          }
          onReset={() =>
            pub.reset.mutate(undefined, {
              onSuccess: () => toast.success('Zurückgesetzt'),
              onError: (e: Error) => toast.error(e.message),
            })
          }
          publishPending={pub.publish.isPending || pub.discard.isPending || pub.reset.isPending}
        />
      </div>
    </AppShell>
  );
}
